import { useEffect, useRef } from "react";
import { Check } from "lucide-react";
import { TextField } from "@/components/apply/Steps";
import { Checkbox } from "@/components/ui/checkbox";
import { formatPhone } from "@/lib/applyApi";

const ROLES = [["owner", "Owner / Seller"], ["trainer", "Trainer / Agent submitting for the owner"]];
const TARGETS = [["owner", "Owner"], ["trainer", "Trainer"], ["both", "Both"]];

function Choice({ id, options, value, onChange }) {
  return (
    <div className={`grid gap-2 ${options.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3"}`} role="radiogroup">
      {options.map(([k, l]) => (
        <button key={k} type="button" role="radio" aria-checked={value === k} data-testid={`${id}-${k}`} onClick={() => onChange(k)}
          className={`min-h-[48px] rounded-xl border-2 px-3 py-2 text-sm font-semibold text-left flex items-center justify-between gap-2 transition-colors duration-200 ${value === k ? "border-stone-900 bg-stone-900 text-white" : "border-stone-300 bg-white text-stone-700 hover:border-stone-500"}`}>
          <span>{l}</span>{value === k && <Check className="h-4 w-4 shrink-0" />}
        </button>
      ))}
    </div>
  );
}

// "Are you the owner or trainer?" + the trainer's own details. In trainer mode the regular contact fields hold the OWNER.
export function SubmitterFields({ s, set, errors = {}, onPick }) {
  const f = (k) => (v) => set({ ...s, [k]: v });
  const picked = useRef(false);
  // After the user picks a role, bring the first contact field into view (only if it's off screen) and focus it.
  useEffect(() => {
    if (!picked.current || !s.role) { return; }
    picked.current = false;
    const t = setTimeout(() => {
      const el = document.getElementById(s.role === "trainer" ? "apply-trainer-sub-name" : "apply-full-name");
      if (!el) { return; }
      const r = el.getBoundingClientRect();
      if (r.top < 0 || r.bottom > window.innerHeight - 100) el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus({ preventScroll: true });
    }, 120);
    return () => clearTimeout(t);
  }, [s.role]);
  return (
    <div data-testid="apply-submitter" className="space-y-4 mb-6">
      <div>
        <p className="text-sm font-semibold text-stone-800 mb-2">Are you the owner or trainer?</p>
        <Choice id="apply-role" options={ROLES} value={s.role} onChange={(role) => { picked.current = true; onPick?.(); set({ ...s, role, contact_target: s.contact_target || "both" }); }} />
      </div>
      {s.role === "trainer" && (
        <div data-testid="apply-trainer-section" className="rounded-xl border border-stone-200 bg-white p-4 space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
          <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Your details (trainer / agent)</p>
          <TextField id="apply-trainer-sub-name" label="Trainer Name" required value={s.trainer_name} onChange={f("trainer_name")} error={errors.trainer_name} autoComplete="name" />
          <TextField id="apply-trainer-sub-email" label="Trainer Email" required type="email" inputMode="email" value={s.trainer_email} onChange={f("trainer_email")} error={errors.trainer_email} autoComplete="email" />
          <TextField id="apply-trainer-sub-phone" label="Trainer Phone" required type="tel" inputMode="tel" value={s.trainer_phone} onChange={f("trainer_phone")} error={errors.trainer_phone}
            onBlur={() => s.trainer_phone && set({ ...s, trainer_phone: formatPhone(s.trainer_phone) })} hint="Include area code, e.g. +1 403 555 0123" />
          <div>
            <p className="text-sm font-semibold text-stone-800 mb-2">Who should we contact about this horse?</p>
            <Choice id="apply-contact-target" options={TARGETS} value={s.contact_target || "both"} onChange={f("contact_target")} />
            <p className="text-xs text-stone-500 mt-1">Used for routine updates. The seller contract and legal notices always go to the owner.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export function OwnerPermission({ s, set, error }) {
  if (s.role !== "trainer") return null;
  return (
    <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
      <Checkbox data-testid="consent-owner-permission" checked={!!s.owner_permission} onCheckedChange={(v) => set({ ...s, owner_permission: !!v })} className="mt-0.5 h-5 w-5" />
      <span className={`text-sm ${error ? "text-red-600" : "text-stone-700"}`}>I confirm I have permission from the owner to submit this horse for sale.</span>
    </label>
  );
}

export function validateSubmitter(s, EMAIL_RE, validPhone, ownerEmail) {
  const e = {};
  if (s.role !== "trainer") return e;
  if (!s.trainer_name?.trim()) e.trainer_name = "Please enter the trainer's name.";
  if (!EMAIL_RE.test(s.trainer_email?.trim() || "")) e.trainer_email = "Please enter a valid trainer email.";
  else if (s.trainer_email.trim().toLowerCase() === (ownerEmail || "").trim().toLowerCase()) e.email = "The owner's email must be different from yours.";
  if (!validPhone(s.trainer_phone)) e.trainer_phone = "Please enter a valid phone number (10–15 digits).";
  return e;
}
